import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom'; 
import useAxios from '../utils/useAxios';
import '../styles/DashboardPage.css';
import { useSidebar } from '../context/SidebarContext';
import NotificationBell from '../components/NotificationBell';
import { useAuth } from '../context/AuthContext';

import {
  FaUserCheck,
  FaUserTimes,
  FaClock,
  FaUsers,
  FaExclamationTriangle,
  FaPercentage,
  FaInfoCircle,
  FaTimes,
  FaCalendarAlt,
  FaBuilding
} from 'react-icons/fa';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import Modal from 'react-modal';

Modal.setAppElement('#root');

const DashboardPage = () => {
  const axiosInstance = useAxios();
  const { sidebarVisible } = useSidebar();
  const isMobile = window.innerWidth < 768;
  const [stats, setStats] = useState({
    total_employes: 0,
    presents: 0,
    absents: 0,
    retards: 0,
    sorties_exceptionnelles: 0,
    taux_ponctualite: 0,
    periode: '',
    heure_limite: '',
  });

  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [selectedPeriode, setSelectedPeriode] = useState('');
  const [loading, setLoading] = useState(true);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [details, setDetails] = useState([]);
  const [detailTitle, setDetailTitle] = useState('');
  const [realTimePresence, setRealTimePresence] = useState([]);
  const { user } = useAuth();
  const [organisations, setOrganisations] = useState([]);
  const [selectedOrganisation, setSelectedOrganisation] = useState('');
  const [showOrganisationDropdown, setShowOrganisationDropdown] = useState(false);
  const [currentOrganisation, setCurrentOrganisation] = useState(null);

  const fetchOrganisations = async () => {
    try {
      const response = await axiosInstance.get('/organisations/');
      setOrganisations(response.data);
      if (response.data.length > 0) {
        const firstOrg = response.data[0];
        setSelectedOrganisation(firstOrg.id);
        setCurrentOrganisation(firstOrg);
      }
    } catch (err) {
      console.error("Erreur lors de la récupération des organisations", err);
    }
  };

  const selectOrganisation = (orgId) => {
    const org = organisations.find(o => o.id === orgId);
    setSelectedOrganisation(orgId);
    setCurrentOrganisation(org);
    setShowOrganisationDropdown(false);
    fetchStats(orgId);
    fetchRealTimePresence(orgId);
  };

  const fetchStats = async (orgId = selectedOrganisation) => {
    if (!orgId) return;
    setLoading(true);
    try {
      const params = { date: selectedDate, organisation: orgId };
      if (selectedPeriode) params.periode = selectedPeriode;
      const { data } = await axiosInstance.get('/pointage/dashboard-stats/', { params });
      setStats(data);
    } catch (error) {
      console.error('Erreur de récupération des stats dashboard :', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDetails = async (type, orgId = selectedOrganisation) => {
    if (!orgId) return;
    try {
      const typeMapping = {
        'total': 'total',
        'sorties_exceptionnelles': 'sorties_exceptionnelles',
        'retards': 'retards',
        'presents': 'presents',
        'absents': 'absents'
      };
      if (!typeMapping[type]) throw new Error(`Type de données non supporté: ${type}`);
      const params = {
        date: selectedDate,
        type: typeMapping[type],
        organisation: orgId,
        ...(selectedPeriode && { periode: selectedPeriode }),
        _: new Date().getTime()
      };
      const { data } = await axiosInstance.get('/pointage/details/', { params, timeout: 10000 });
      if (!Array.isArray(data)) throw new Error("Réponse serveur invalide");
      setDetails(data);
      setDetailTitle(getDetailTitle(type));
      setModalIsOpen(true);
    } catch (error) {
      console.error('Erreur fetchDetails:', error);
      setDetails([]);
      setDetailTitle(getDetailTitle(type));
      setModalIsOpen(true);
    }
  };

  const fetchRealTimePresence = async (orgId = selectedOrganisation) => {
    if (!orgId) return;
    try {
      const { data } = await axiosInstance.get('/pointage/current-presence/', {
        params: { date: selectedDate, organisation: orgId, _: new Date().getTime() },
        timeout: 8000
      });
      if (!Array.isArray(data)) { setRealTimePresence([]); return; }
      setRealTimePresence(data);
    } catch (error) {
      console.error('Erreur fetchRealTimePresence:', error);
      setRealTimePresence([]);
    }
  };

  const getDetailTitle = (type) => {
    switch(type) {
      case 'presents': return 'Liste des employés présents';
      case 'absents': return 'Liste des employés absents';
      case 'retards': return 'Liste des employés en retard';
      case 'sorties_exceptionnelles': return 'Liste des sorties exceptionnelles';
      default: return 'Détails';
    }
  };

  useEffect(() => { fetchOrganisations(); }, []);

  useEffect(() => {
    if (selectedOrganisation) {
      fetchStats();
      fetchRealTimePresence();
      const interval = setInterval(() => {
        fetchStats();
        fetchRealTimePresence();
      }, 60000);
      return () => clearInterval(interval);
    }
  }, [selectedDate, selectedPeriode, selectedOrganisation]);

  const styles = {
    container: {
      marginLeft: sidebarVisible ? 250 : 0,
      padding: '20px',
      backgroundColor: '#f8fafc',
      minHeight: '100vh',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    },
  };

  // ── Permission carte RH ──────────────────────────────────
  const canViewGestionAbsences =
    user?.is_super_admin ||
    user?.permissions?.includes('VIEW_LEAVE_REQUESTS') ||
    user?.permissions?.includes('VIEW_LATE_REPORTS');

  return (
    <div style={styles.container}>
      <div className="dashboard-header">
        <h3>Tableau de Bord RH</h3>
        {stats.periode && (
          <p className="sub-header">
            Période actuelle : {stats.periode.toUpperCase()} {stats.heure_limite && `(Heure limite: ${stats.heure_limite})`}
          </p>
        )}
        <hr className="header-divider" />
      </div>

      {/* ── SÉLECTEUR ORGANISATION ── */}
      <div style={{
        backgroundColor: '#fff',
        padding: '20px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        marginBottom: '30px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowOrganisationDropdown(!showOrganisationDropdown)}
            style={{
              padding: '12px 20px',
              border: '2px solid #1B019B',
              borderRadius: '8px',
              backgroundColor: '#fff',
              color: '#1B019B',
              fontWeight: '600',
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: 'pointer',
              minWidth: '300px'
            }}
          >
            <FaBuilding />
            {currentOrganisation ? currentOrganisation.nom : 'Chargement...'}
            {showOrganisationDropdown ? <FiChevronUp /> : <FiChevronDown />}
          </button>

          {showOrganisationDropdown && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              backgroundColor: '#fff',
              border: '2px solid #1B019B',
              borderTop: 'none',
              borderRadius: '0 0 8px 8px',
              maxHeight: '200px',
              overflowY: 'auto',
              zIndex: 1000
            }}>
              {organisations.map(org => (
                <div
                  key={org.id}
                  onClick={() => selectOrganisation(org.id)}
                  style={{
                    padding: '12px 20px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #eee',
                    backgroundColor: selectedOrganisation === org.id ? '#f0f0ff' : '#fff',
                    color: selectedOrganisation === org.id ? '#1B019B' : '#333',
                    fontWeight: selectedOrganisation === org.id ? '600' : '400'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9ff'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = selectedOrganisation === org.id ? '#f0f0ff' : '#fff'}
                >
                  {org.nom}
                </div>
              ))}
            </div>
          )}
        </div>

        {currentOrganisation && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ color: '#666', fontSize: '14px' }}>
              Statistiques pour cette organisation
            </span>
          </div>
        )}
      </div>

      {currentOrganisation ? (
        <>
          {/* ── FILTRES ── */}
          <div className="filters-container">
            <div className="filter-group">
              <label className="filter-label">Date</label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="filter-select"
              />
            </div>
            <div className="filter-group">
              <label className="filter-label">Période</label>
              <select
                value={selectedPeriode}
                onChange={(e) => setSelectedPeriode(e.target.value)}
                className="filter-select"
              >
                <option value="">Période actuelle (auto)</option>
                <option value="matin">Matin</option>
                <option value="apres_midi">Après-midi</option>
              </select>
            </div>
            <NotificationBell />
          </div>

          {loading ? (
            <div className="cards-container">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="card-skeleton" />
              ))}
            </div>
          ) : (
            <>
              {/* ── CARTES STATS ── */}
              <div className="cards-container">
                <Card icon={<FaUsers />} text="Employés inscrits" value={stats.total_employes} color="#007bff" onDetailClick={() => fetchDetails('total')} />
                <Card icon={<FaUserCheck />} text="Présents" value={stats.presents} color="#28a745" onDetailClick={() => fetchDetails('presents')} />
                <Card icon={<FaUserTimes />} text="Absents" value={stats.absents} color="#dc3545" onDetailClick={() => fetchDetails('absents')} />
                <Card icon={<FaClock />} text="Retards" value={stats.retards} color="#ffc107" onDetailClick={() => fetchDetails('retards')} />
                <Card icon={<FaExclamationTriangle />} text="Sorties exceptionnelles" value={stats.sorties_exceptionnelles} color="#17a2b8" onDetailClick={() => fetchDetails('sorties_exceptionnelles')} />
                <Card icon={<FaPercentage />} text="Ponctualité (%)" value={`${stats.taux_ponctualite}%`} color="#6610f2" />
              </div>

              {/* ── PRÉSENCES TEMPS RÉEL ── */}
              <div className="realtime-container">
                <div className="realtime-header">
                  <div className="realtime-icon">
                    <FaUserCheck size={20} />
                  </div>
                  <h4 className="realtime-title">
                    {[...new Set(realTimePresence.map(emp => emp.id))].length} employés présents le {selectedDate}
                    {selectedDate === new Date().toISOString().split('T')[0] && (
                      <span className="realtime-subtitle">(en temps réel)</span>
                    )}
                  </h4>
                </div>

                {realTimePresence.length > 0 ? (
                  <div className="realtime-list">
                    {realTimePresence
                      .filter((emp, index, self) => index === self.findIndex(e => e.id === emp.id))
                      .map((employe) => {
                        const pointages = realTimePresence.filter(e => e.id === employe.id);
                        return (
                          <div key={employe.id} className="realtime-card">
                            <div className="employee-avatar">
                              {employe.nom.charAt(0)}{employe.prenom.charAt(0)}
                            </div>
                            <div className="employee-info">
                              <div className="employee-name">{employe.nom} {employe.prenom}</div>
                              <div className="employee-position">{employe.poste}</div>
                              {pointages[0].heure_arrivee && (
                                <div className="employee-time">
                                  <FaClock size={12} />
                                  {pointages.length > 1
                                    ? `${pointages.length} passages (dont ${pointages[0].heure_arrivee})`
                                    : `Arrivé à ${pointages[0].heure_arrivee}`}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                ) : (
                  <div className="no-data">
                    <FaUserTimes size={40} />
                    <p>Aucun employé présent pour cette date</p>
                  </div>
                )}
              </div>

              {/* ── ACTIONS RH ── */}
              {canViewGestionAbsences && (
                <div className="rh-actions">
                  <h3>Actions RH</h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '16px',
                    marginTop: '16px',
                  }}>
                    <Link
                      to="/gestion-absences-retards"
                      style={{
                        backgroundColor: '#fff',
                        border: '1px solid #e0e0e0',
                        borderTop: '3px solid #1B019B',
                        borderRadius: '10px',
                        padding: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        textDecoration: 'none',
                        cursor: 'pointer',
                        transition: 'box-shadow 0.2s',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(27,1,155,0.15)'}
                      onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
                    >
                      <FaCalendarAlt size={22} color="#1B019B" />
                      <span style={{ fontSize: '14px', fontWeight: '600', color: '#1B019B' }}>
                        Absences & Retards
                      </span>
                      <span style={{ fontSize: '12px', color: '#888' }}>
                        Gérer les demandes et signalements
                      </span>
                    </Link>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── MODAL DÉTAILS ── */}
          <Modal
            isOpen={modalIsOpen}
            onRequestClose={() => setModalIsOpen(false)}
            style={{
              content: {
                top: '50%',
                left: isMobile ? '50%' : `calc(50% + ${sidebarVisible ? 125 : 0}px)`,
                right: 'auto',
                bottom: 'auto',
                transform: 'translate(-50%, -50%)',
                width: '60%',
                maxWidth: '700px',
                maxHeight: '70vh',
                overflow: 'auto',
                borderRadius: '10px',
                border: 'none',
                boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
                padding: '20px',
              },
              overlay: {
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 1000,
                marginLeft: sidebarVisible ? 250 : 0,
              },
            }}
            contentLabel="Détails"
          >
            <div className="modal-header">
              <h2>{detailTitle}</h2>
              <button onClick={() => setModalIsOpen(false)} className="modal-close">
                <FaTimes />
              </button>
            </div>
            <div className="modal-content">
              {details.length > 0 ? (
                <div className="details-table-container">
                  <table className="details-table">
                    <thead>
                      <tr>
                        <th>Nom</th>
                        <th>Poste</th>
                        {details[0].heure_arrivee && <th>Heure</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {details.map((item, index) => (
                        <tr key={index}>
                          <td>{item.nom} {item.prenom}</td>
                          <td>{item.poste}</td>
                          {item.heure_arrivee && <td>{item.heure_arrivee}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="no-data">
                  <FaInfoCircle size={30} />
                  <p>Aucune donnée disponible</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button onClick={() => setModalIsOpen(false)} className="close-button">
                Fermer
              </button>
            </div>
          </Modal>
        </>
      ) : (
        <div className="text-center py-5">
          <p className="text-muted">Chargement des organisations...</p>
        </div>
      )}
    </div>
  );
};

const Card = ({ icon, text, value, color, onDetailClick }) => (
  <div className="dashboard-card" style={{ borderTopColor: color }}>
    <div className="card-content">
      <div className="card-icon" style={{ color }}>{icon}</div>
      <div className="card-value">{value}</div>
      <div className="card-label">{text}</div>
    </div>
    {onDetailClick && (
      <button className="detail-button" onClick={onDetailClick}>
        <FaInfoCircle /> Voir détails
      </button>
    )}
  </div>
);

export default DashboardPage;